import dayjs from 'dayjs';

export const formatPriceToKorean = (value: number) => {
  const absoluteValue = Math.abs(value);

  if (absoluteValue === 0) {
    return '0원';
  }

  if (absoluteValue >= 10000) {
    const billion = Math.floor(absoluteValue / 10000);
    const tenMillion = Math.floor((absoluteValue % 10000) / 1);

    return `${billion}억 ${
      tenMillion ? `${tenMillion.toLocaleString()}만원` : ''
    }`;
  }

  if (absoluteValue < 10000) {
    const tenMillion = Math.floor(absoluteValue / 1000);
    const million = Math.floor(absoluteValue / 10);

    return `${absoluteValue.toLocaleString()}만원`;
  }

  return absoluteValue.toLocaleString();
};

export const formatDanjiAveragePriceTick = (value: number) => {
  const isNegative = value < 0;
  const absoluteValue = Math.abs(value);

  if (absoluteValue === 0) {
    return '0원';
  }

  if (absoluteValue >= 10000) {
    const billions = Math.floor(absoluteValue / 10000);
    const millions = Math.floor((absoluteValue % 10000) / 100);
    const formattedValue = `${billions}.${String(millions).padStart(2, '0')}억`;
    return isNegative ? `-${formattedValue}` : formattedValue;
  }

  if (absoluteValue < 10000) {
    const tenMillion = Math.floor(absoluteValue / 1000);
    const million = Math.floor((absoluteValue % 1000) / 100);
    let formattedValue = `${tenMillion}.${million}천`;

    // 0.7천, 0.5천과 같은 경우 앞의 0을 제거
    if (tenMillion === 0) {
      formattedValue = `${million}천`;
    }

    return isNegative ? `-${formattedValue}` : formattedValue;
  }

  return absoluteValue.toLocaleString();
};

export const formatNumberToKoreanRealTrade = (value: number) => {
  if (value === 0) {
    return '0원';
  }

  if (value >= 10000) {
    const billion = Math.floor(value / 10000);
    const tenMillion = Math.floor((value % 10000) / 1000);

    return `${billion}.${tenMillion}억`;
  }

  if (value < 10000) {
    const tenMillion = Math.floor(value / 1000);
    const million = Math.floor(value / 100) % tenMillion;

    return `${tenMillion}.${million}천`;
  }

  return value.toLocaleString();
};

export const formatTradeTypeValueToKorean = (tradeType: string) => {
  switch (tradeType) {
    default:
      return '';
    case 'TRADE':
      return '매매';
    case 'RENTAL':
      return '전세';
    case 'MONTHLY_RENTAL':
      return '월세';
  }
};

export function generateMonthArray(
  startDate: string,
  endDate: string,
  format = 'YYYYMM',
) {
  const start = dayjs(startDate).startOf('month');
  const end = dayjs(endDate).endOf('month');
  const monthArray: string[] = [];

  let current = start;

  while (current.isBefore(end) || current.isSame(end, 'month')) {
    monthArray.push(current.format(format));

    current = current.add(1, 'month');
  }

  return monthArray;
}

export function generateDayArray(
  startDate: string,
  endDate: string,
  format = 'YYYYMMDD',
) {
  const start = dayjs(startDate);
  const end = dayjs(endDate);
  const dayArray: string[] = [];

  let current = start;

  while (current.isBefore(end) || current.isSame(end)) {
    dayArray.push(current.format(format));

    current = current.add(1, 'day');
  }

  return dayArray;
}

function findClosestNumber(target: number, numbers: number[]): number {
  // 인접한 숫자를 저장할 변수 초기화
  let closestNumber: number = numbers[0];

  // 타겟과 인접한 숫자를 찾기
  for (let i = 1; i < numbers.length; i += 1) {
    if (Math.abs(target - numbers[i]) < Math.abs(target - closestNumber)) {
      closestNumber = numbers[i];
    }
  }

  return closestNumber;
}
interface ParamsTick {
  min: number;
  max: number;
  tickCount?: number;
  numbers?: number[];
}

export function chartYtickFormatter({
  min,
  max,
  tickCount = 4,
  numbers = [100, 300, 500, 1000, 1500, 3000],
}: ParamsTick) {
  if (min === 0 && max === 0) {
    return {
      domainMax: 'auto',
      domainMin: 'auto',
      domain: ['auto', 'auto'],
      ticks: undefined,
    };
  }

  const diff = max - min;
  const yScale = findClosestNumber(diff, numbers); // 가장 가까운 yScale 값을 찾습니다.

  const unit = String(yScale).length - 1;
  const numberOfDigit = 10 ** unit;

  // 최소값과 최대값을 yScale에 맞게 조정합니다.
  const roundedMin = Math.ceil(min / numberOfDigit) * numberOfDigit;
  const roundedMax = Math.floor(max / numberOfDigit) * numberOfDigit;

  // domainMin과 domainMax를 계산합니다.
  const domainMin = roundedMin - yScale;
  const domainMax = roundedMax + yScale;

  // Ticks 배열을 생성합니다.
  const totalRange = domainMax - domainMin;
  const tickInterval = totalRange / (tickCount - 1);
  const ticks = Array.from(
    { length: tickCount },
    (_, i) => domainMin + i * tickInterval,
  );

  return {
    domainMax: ticks[ticks.length - 1],
    domainMin: ticks[0],
    ticks,
    domain: [ticks[0], ticks[ticks.length - 1]],
  };
}

export const getRangePrice = ({ min, max, tickCount = 5 }: ParamsTick) => {
  if (min === 0 && max === 0) {
    return { ticks: [], range: 0 };
  }

  const ticks: number[] = [];

  if (min === max) {
    const baseValue = min;
    const offset = baseValue * 0.1; // 10% 단위로 범위 설정
    const start = baseValue - ((tickCount - 1) / 2) * offset;
    for (let i = 0; i <= tickCount; i += 1) {
      ticks.push(start + i * offset);
    }
    return { ticks, range: offset };
  }

  // ticks 배열의 첫 번째 요소로 min을 추가
  ticks.push(min);

  // 중간 tick을 추가
  const range = (max - min) / (tickCount - 1);
  for (let i = 1; i <= tickCount - 1; i += 1) {
    ticks.push(min + range * i);
  }

  // ticks 배열의 마지막 요소로 max를 추가
  ticks.push(max + range);

  return { ticks, range };
};

export const getMinMax = (numbers: number[]) => {
  return {
    min: Math.min(...numbers),
    max: Math.max(...numbers),
  };
};
